import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import AssessmentEntry from "@/pages/AssessmentEntry";
import ParticipantIdentityGate from "@/components/assessment/ParticipantIdentityGate";
import DailyCompanionExperienceV3 from "@/components/daily/DailyCompanionExperienceV3";
import { getSavedAssessment } from "@/components/assessment/savedAssessmentAdapter";

const CompanionAssessmentHeader = ({ hasAssessment }) => (
  <header
    className="sticky top-0 z-50 border-b backdrop-blur-xl"
    style={{
      backgroundColor: "rgba(248, 247, 242, 0.94)",
      borderColor: "var(--forr-line-soft)",
    }}
  >
    <div className="mx-auto flex min-h-[68px] max-w-[920px] items-center gap-3 px-4 sm:px-6">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-[17px]"
        style={{
          background:
            "linear-gradient(145deg, var(--forr-teal-deep), var(--forr-teal))",
        }}
      >
        <Sparkles size={20} color="#fff" />
      </div>
      <div>
        <div className="font-display text-[17px] font-medium forr-text-ink">
          APOS Companion
        </div>
        <div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] forr-text-mute">
          {hasAssessment
            ? "Add a new nervous-system baseline assessment"
            : "Build your nervous-system baseline first"}
        </div>
      </div>
    </div>
  </header>
);

const DailyApp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(() => getSavedAssessment());

  useEffect(() => {
    const refreshAssessment = () => {
      const next = getSavedAssessment();
      if (next) setAssessment(next);
    };
    const profileSaved = () => {
      const next = getSavedAssessment();
      if (!next) return;
      setAssessment(next);
      navigate("/app", { replace: true });
    };
    const profileCleared = () => {
      // Retaking begins a new assessment without deleting or deactivating the
      // prior baseline. The previous assessment remains available until the
      // new result is completed and appended to history.
      refreshAssessment();
      navigate("/app/assessment", { replace: true });
    };

    window.addEventListener("adaptive-profile-saved", profileSaved);
    window.addEventListener("adaptive-participant-identity-saved", refreshAssessment);
    window.addEventListener("adaptive-profile-cleared", profileCleared);
    window.addEventListener("adaptive-active-assessment-changed", refreshAssessment);
    window.addEventListener("apos-cloud-data-hydrated", refreshAssessment);
    return () => {
      window.removeEventListener("adaptive-profile-saved", profileSaved);
      window.removeEventListener("adaptive-participant-identity-saved", refreshAssessment);
      window.removeEventListener("adaptive-profile-cleared", profileCleared);
      window.removeEventListener("adaptive-active-assessment-changed", refreshAssessment);
      window.removeEventListener("apos-cloud-data-hydrated", refreshAssessment);
    };
  }, [navigate]);

  useEffect(() => {
    if (!assessment) return;
    window.dispatchEvent(
      new CustomEvent("adaptive-companion-opened", {
        detail: { completedAt: assessment.completedAt },
      }),
    );
  }, [assessment]);

  const assessmentRoute = location.pathname === "/app/assessment";
  if (!assessment || assessmentRoute) {
    return (
      <div
        className="min-h-screen"
        style={{
          background:
            "linear-gradient(180deg, #F4F7F4 0%, #F8F6F0 48%, #F4F1E9 100%)",
        }}
      >
        <CompanionAssessmentHeader hasAssessment={Boolean(assessment)} />
        <AssessmentEntry />
      </div>
    );
  }

  if (!assessment.identity) {
    return (
      <ParticipantIdentityGate>
        <div className="min-h-screen forr-canvas" />
      </ParticipantIdentityGate>
    );
  }

  return <DailyCompanionExperienceV3 assessment={assessment} />;
};

export default DailyApp;
